import type {
  PublicSupplier,
  PublicSupplierCertificate
} from '@tandemone/types'
import { FileCheck2, Globe, Sparkles } from 'lucide-react'
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { normalizeWebsite, websiteLabel } from '@/lib/url'

import { CertBadge } from './cert-badge'
import { sortByCertFamily } from './cert-family'
import { SupplierStatusBadge } from './supplier-status-badge'

type Props = {
  companyName: string
  supplier: PublicSupplier
  logoUrl: string | null
  certificates: PublicSupplierCertificate[]
  isLicensedExporter: boolean
  /** when provided, renders instead of the default status pill. used by the
   * public supplier profile to show verified/not-verified rather than the
   * raw lifecycle status.
   */
  statusBadge?: ReactNode
  /** staff-only label after the status pill. */
  superBadge?: ReactNode
  favoriteAction?: ReactNode
  rfiAction?: ReactNode
  superAction?: ReactNode
}

type MetaItem = { key: string; node: ReactNode }

export function ProfileHero({
  companyName,
  supplier,
  logoUrl,
  certificates,
  isLicensedExporter,
  statusBadge,
  superBadge,
  favoriteAction,
  rfiAction,
  superAction
}: Props) {
  const initials = companyName
    .split(/\s+/)
    .map(part => part[0]?.toUpperCase() ?? '')
    .filter(Boolean)
    .slice(0, 2)
    .join('')

  const sortedCertificates = sortByCertFamily(certificates)

  const logoEl = logoUrl ? (
    <img
      src={logoUrl}
      alt={`${companyName} logo`}
      className='size-20 rounded-full border border-border object-cover'
    />
  ) : (
    <div className='grid size-20 place-items-center rounded-full bg-accent text-xl font-semibold text-white'>
      {initials || '?'}
    </div>
  )

  /** gray, dot-separated facts. the founding tag renders separately (and
   * first) since it's an accent pill, not a plain fact.
   */
  const facts: (MetaItem | null)[] = [
    supplier.location ? { key: 'location', node: supplier.location } : null,
    supplier.yearFounded
      ? { key: 'founded', node: `Founded ${supplier.yearFounded}` }
      : null,
    supplier.website
      ? { key: 'website', node: <WebsiteLink website={supplier.website} /> }
      : null,
    supplier.isPublic ? { key: 'public', node: 'Public company' } : null
  ]
  const metaItems = facts.filter((item): item is MetaItem => item !== null)

  return (
    <div className='-mx-4 flex flex-col gap-4 border-b border-border bg-page p-4 sm:sticky sm:top-14 sm:z-10 sm:-mx-6 sm:flex-row sm:items-start sm:gap-6 sm:p-6'>
      <div className='shrink-0'>{logoEl}</div>

      <div className='min-w-0 flex-1 space-y-2'>
        <div className='flex flex-col items-start gap-1.5 leading-tight'>
          <div className='flex flex-wrap items-center gap-2'>
            {supplier.isFoundingSupplier ? <FoundingTag /> : null}
            {statusBadge ?? <SupplierStatusBadge status={supplier.status} />}
            {superBadge}
          </div>
          <h1 className='text-2xl font-semibold text-foreground'>
            {companyName}
          </h1>
        </div>
        <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground'>
          {metaItems.map((item, index) => (
            <span key={item.key} className='flex items-center gap-x-3'>
              {index > 0 ? <span aria-hidden='true'>·</span> : null}
              {item.node}
            </span>
          ))}
        </div>
        {sortedCertificates.length > 0 || isLicensedExporter || superAction ? (
          <div className='flex flex-col items-stretch gap-3 sm:flex-row sm:items-center'>
            {/** the exporter tag leads the row so the "+N more" overflow never hides it. */}
            <div className='flex min-w-0 flex-1 items-start gap-1.5'>
              {isLicensedExporter ? <LicensedExporterTag /> : null}
              {sortedCertificates.length > 0 ? (
                <div className='min-w-0 flex-1'>
                  <HeroCertificateBadges certificates={sortedCertificates} />
                </div>
              ) : null}
            </div>
            {superAction ? (
              <div className='flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row [&>button]:w-full sm:[&>button]:w-auto'>
                {superAction}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {favoriteAction || rfiAction ? (
        <div className='flex w-full shrink-0 gap-2 sm:w-auto sm:self-end [&>*]:flex-1 sm:[&>*]:flex-none'>
          {favoriteAction}
          {rfiAction}
        </div>
      ) : null}
    </div>
  )
}

/** prominent founding marker that sits beside the verified badge. built on
 * the same Badge primitive with the same size overrides as the lg verified
 * badge so the two pills match height, padding, and icon size exactly.
 */
function FoundingTag() {
  return (
    <Badge className='h-6 gap-1 border-transparent bg-accent-light px-2.5 text-xs text-accent dark:text-accent-mid [&>svg]:size-3.5!'>
      <Sparkles />
      Founding supplier
    </Badge>
  )
}

/** cyan is unused by cert families. mt-1 matches the cert row's top padding. */
function LicensedExporterTag() {
  return (
    <Badge className='mt-1 shrink-0 gap-1 border-transparent bg-cyan-100 text-cyan-900 dark:bg-cyan-950 dark:text-cyan-200 [&>svg]:size-3'>
      <FileCheck2 />
      Licensed Exporter
    </Badge>
  )
}

/* the stored value is normalized on save, but legacy rows predate that, so
 * normalize again here rather than trust the column. */
function WebsiteLink({ website }: { website: string }) {
  return (
    <a
      href={normalizeWebsite(website)}
      target='_blank'
      rel='noopener noreferrer'
      className='inline-flex min-w-0 items-center gap-1 font-medium text-accent hover:underline dark:text-accent-mid'
    >
      <Globe className='size-3.5 shrink-0' aria-hidden='true' />
      <span className='truncate'>{websiteLabel(website)}</span>
    </a>
  )
}

const BADGE_GAP = 6 // matches gap-1.5 in tailwind

function HeroCertificateBadges({
  certificates
}: {
  certificates: PublicSupplierCertificate[]
}) {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const mirrorRef = useRef<HTMLDivElement | null>(null)
  const [visibleCount, setVisibleCount] = useState(certificates.length)

  useLayoutEffect(() => {
    function recompute() {
      const wrapper = wrapperRef.current
      const mirror = mirrorRef.current
      if (!wrapper || !mirror) return
      const items = Array.from(mirror.children) as HTMLElement[]
      if (items.length === 0) return
      const moreItem = items[items.length - 1]!
      const moreWidth = moreItem.offsetWidth
      const realItems = items.slice(0, -1)
      const containerWidth = wrapper.clientWidth

      const totalWidth = realItems.reduce(
        (sum, el, idx) => sum + el.offsetWidth + (idx > 0 ? BADGE_GAP : 0),
        0
      )
      if (totalWidth <= containerWidth) {
        setVisibleCount(realItems.length)
        return
      }

      const budget = containerWidth - BADGE_GAP - moreWidth
      let used = 0
      let count = 0
      for (let i = 0; i < realItems.length; i++) {
        const width = realItems[i]!.offsetWidth + (count > 0 ? BADGE_GAP : 0)
        if (used + width > budget) break
        used += width
        count++
      }
      setVisibleCount(count)
    }
    recompute()
    const observer = new ResizeObserver(recompute)
    if (wrapperRef.current) observer.observe(wrapperRef.current)
    return () => observer.disconnect()
  }, [certificates])

  const hiddenCount = Math.max(0, certificates.length - visibleCount)

  return (
    <div ref={wrapperRef} className='relative pt-1'>
      {/** offscreen mirror used only for offsetWidth measurement.
       * overflow-hidden is required so the shrink-0 + flex-nowrap
       * children don't propagate horizontal overflow up the tree.
       */}
      <div
        ref={mirrorRef}
        aria-hidden
        className='pointer-events-none invisible absolute inset-x-0 top-1 flex flex-nowrap gap-1.5 overflow-hidden'
      >
        {certificates.map(cert => (
          <CertBadge
            key={`mirror-${cert.id}`}
            certification={cert.certification}
          />
        ))}
        <Badge variant='outline' className='shrink-0 text-muted-foreground'>
          +{certificates.length} more
        </Badge>
      </div>

      <div className='flex flex-nowrap items-center gap-1.5 overflow-hidden'>
        {certificates.slice(0, visibleCount).map(cert => (
          <CertBadge key={cert.id} certification={cert.certification} />
        ))}
        {hiddenCount > 0 ? (
          <Badge variant='outline' className='shrink-0 text-muted-foreground'>
            +{hiddenCount} more
          </Badge>
        ) : null}
      </div>
    </div>
  )
}
